import { find, isEmpty } from 'lodash';
import { notificationIdentity } from '../definitions/NotificationConstants';
import { toastIdentity } from '../definitions/ToastConstants';
import moment from 'moment';
import { sortByTimeNewestFirst } from '../utils/SortUtils';
import { GTIFilter } from '../definitions/GtiConstants';
import { getTime } from '../utils/TimeUtils';

/**
 * Creates common action wrapper for the current reducer.
 * @param {String} reducerName The reducer name (usually in the form "my/foo/reducer")
 * @return {Function} The resulting function for action name generation.
 */
export const createActionName =
  (reducerName) =>
  /**
   * Creates action name prepended with the redurer name given earlier
   * @param {String} actionName The action name (usually in the form of "ACTION_NAME")
   * @return {String} The resulting action name.
   */
  (actionName) =>
    `${reducerName}/${actionName}`;

/**
 * Check that given parameter is a valid value.
 * Null or undefined values will return false,
 * others true.
 * */
export const isValue = (value) => {
  return value !== null && value !== undefined;
};

/**
 * Creates an identity function which returns true if the field
 * of the item equals given value.
 *
 * @param {String} field The field to be matched
 * @param {Object} value The value to be matched
 * @param {Object} item  The item to be searched
 */
export const identity = (field) => (value) => (item) => item[field] === value;

/**
 * returns found item on a list parametrized by given
 */
export const findByIdentity = (field) => (list, key) => {
  return find(list, identity(field)(key));
};

/**
 * Find notification by identity. (notificationKey)
 * */
export const findByNotificationIdentity = findByIdentity(notificationIdentity);

export const findByToastIdentity = findByIdentity(toastIdentity);

export const executeAllPromises = (promises) => {
  // Wrap all Promises in a Promise that will always "resolve"
  let resolvingPromises = promises.map(function (promise) {
    return new Promise(function (resolve) {
      let payload = new Array(2);
      promise
        .then(function (result) {
          payload[0] = result;
        })
        .catch(function (error) {
          payload[1] = error;
        })
        .then(function () {
          /*
           * The wrapped Promise returns an array:
           * The first position in the array holds the result (if any)
           * The second position in the array holds the error (if any)
           */
          resolve(payload);
        });
    });
  });

  let errors = [];
  let results = [];

  // Execute all wrapped Promises
  return Promise.all(resolvingPromises).then(function (items) {
    items.forEach(function (payload) {
      if (payload[1]) {
        errors.push(payload[1]);
      } else {
        results.push(payload[0]);
      }
    });

    return {
      errors: errors,
      results: results,
    };
  });
};

/**
 * Calculates gti appointment status.
 *
 * gti appointment status is "active" if it has not yet started and is within a week
 * "inactive" if after a week
 * null if it has started
 *
 * @param appointment
 * @returns {*}
 */
export const getGtiAppointmentStatus = (appointment) => {
  let appointmentDate = getTime(appointment.date);
  let today = getTime();

  // check that appointment date is within a week (including today and the day within a week)
  if (
    appointmentDate.isBetween(today, today.clone().add(7, 'days'), 'day', '[]')
  ) {
    return appointment.id;
  } else {
    return null;
  }
};

export const getGtiAppointment = (items) => {
  let today = getTime();

  let upcomingGtiAppointments = items.filter(
    (appointment) =>
      GTIFilter(appointment) &&
      today.startOf('day').isBefore(getTime(appointment.date))
  );

  upcomingGtiAppointments = upcomingGtiAppointments
    .sort(sortByTimeNewestFirst('date'))
    .filter((appointment) => getGtiAppointmentStatus(appointment) != null);

  if (!isEmpty(upcomingGtiAppointments)) {
    return upcomingGtiAppointments[0].id;
  }

  return 'GTI_ACTIVE';
};
